/**
 * Badge selection helpers — pure, framework-free logic shared by BadgeList and
 * any caller that needs to order/dedup/overlay badges before rendering.
 *
 * Keeping this separate from the .tsx components means the ordering + overflow
 * rules (the part with actual logic) are unit-testable in a plain node env
 * without a DOM or JSX transform.
 *
 * See docs/Page-Types-And-Templates.md §4.2 (ordering & dedup) and §4.3
 * (seen_before client overlay).
 */

import { BADGE_CONFIGS, makeBadge, type Badge } from './config'

export type { Badge } from './config'

/**
 * Sort badges ascending by priority and remove duplicates by `type`
 * (keeping the first / highest-priority occurrence). Returns a new array;
 * never mutates the input. Null/undefined input → [].
 *
 * Identical input arrays always yield identical ordered output, which is the
 * whole point: the same product renders the same badge order everywhere.
 */
export function sortBadges(badges: Badge[] | null | undefined): Badge[] {
  if (!badges || badges.length === 0) return []

  // Sort first (stable), then dedup keeping the first occurrence per type.
  const sorted = [...badges].sort((a, b) => a.priority - b.priority)
  const seen = new Set<string>()
  const out: Badge[] = []
  for (const badge of sorted) {
    if (seen.has(badge.type)) continue
    seen.add(badge.type)
    out.push(badge)
  }
  return out
}

/**
 * Apply the per-surface max as a simple priority slice. Overflow is ALWAYS a
 * priority slice — never a different selection rule (spec §5).
 */
export function applyMax(badges: Badge[], max: number): Badge[] {
  if (max <= 0) return []
  return badges.slice(0, max)
}

/**
 * Client-only `seen_before` overlay (spec §4.3). The server never emits
 * `seen_before`; if the visitor has recently viewed this product, prepend it to
 * the server-provided array and re-sort by priority. This is the ONLY permitted
 * client-side mutation of the badge set.
 */
export function withSeenBefore(
  serverBadges: Badge[] | null | undefined,
  hasSeenBefore: boolean
): Badge[] {
  const base = serverBadges ?? []
  if (!hasSeenBefore) return sortBadges(base)
  // Guard against double-injection if the array already carries it.
  if (base.some((b) => b.type === 'seen_before')) return sortBadges(base)
  return sortBadges([...base, makeBadge('seen_before')])
}

/**
 * Convenience: turn a legacy `BadgeType[]` (from determineBadges) into a
 * canonical `Badge[]`. Lets the old client engine feed the new components
 * during the migration window. Filters out null/unknown types.
 */
export function badgesFromTypes(types: Array<string | null | undefined>): Badge[] {
  const out: Badge[] = []
  for (const t of types) {
    if (t && t in BADGE_CONFIGS) {
      out.push(makeBadge(t as Badge['type']))
    }
  }
  return sortBadges(out)
}
